import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSION_MODULES as PM } from '../constants/permissionModules';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { AthleteAvatar } from '../components/AthleteAvatar';
import { ReportStatusBadge } from '../components/devreport/shared';
import CreateReportDialog from '../components/devreport/CreateReportDialog';
import { fmtDate, fmtDateTime } from '../components/devreport/constants';
import { FileText, Plus, MoreVertical, Copy, Archive, ArchiveRestore, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DevelopmentReports = () => {
  const navigate = useNavigate();
  const { user, hasModuleAccess } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const canEdit = isAdmin || hasModuleAccess(PM.DEVELOPMENT_REPORTS, 'edit');
  const canDuplicate = isAdmin || hasModuleAccess(PM.DEVELOPMENT_REPORTS_DUPLICATE, 'edit');
  const canArchive = isAdmin || hasModuleAccess(PM.DEVELOPMENT_REPORTS_ARCHIVE, 'edit');
  const canRestore = isAdmin || hasModuleAccess(PM.DEVELOPMENT_REPORTS_RESTORE, 'edit');
  const canDeleteDraft = isAdmin || hasModuleAccess(PM.DEVELOPMENT_REPORTS_DELETE_DRAFT, 'edit');

  const [reports, setReports] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [athleteFilter, setAthleteFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const athleteMap = useMemo(() => Object.fromEntries(athletes.map((a) => [a.id, a])), [athletes]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const [r, a] = await Promise.all([
        axios.get(`${API}/development-reports`, { params }),
        axios.get(`${API}/athletes`).catch(() => ({ data: [] })),
      ]);
      setReports(Array.isArray(r.data) ? r.data : []);
      setAthletes(Array.isArray(a.data) ? a.data : (a.data?.athletes || []));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load development reports');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/seasons`).then((r) => setSeasons(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);

  const visible = useMemo(() => reports.filter((rep) => athleteFilter === 'all' || rep.athlete_id === athleteFilter), [reports, athleteFilter]);

  const create = async (payload) => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/development-reports`, payload);
      toast.success('Draft created');
      setShowCreate(false);
      navigate(`/development-reports/${res.data.id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create report');
    } finally {
      setCreating(false);
    }
  };

  const openReport = (rep) => {
    if (rep.status === 'draft') navigate(`/development-reports/${rep.id}/edit`);
    else navigate(`/development-reports/${rep.id}`);
  };

  const duplicate = async (rep) => {
    setBusyId(rep.id);
    try {
      const res = await axios.post(`${API}/development-reports/${rep.id}/duplicate`);
      toast.success('Duplicated as a new draft');
      navigate(`/development-reports/${res.data.id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate failed');
    } finally {
      setBusyId(null);
    }
  };

  const archive = async (rep) => {
    if (!window.confirm('Archive this report? It remains viewable but is clearly marked Archived.')) return;
    setBusyId(rep.id);
    try {
      await axios.post(`${API}/development-reports/${rep.id}/archive`);
      toast.success('Report archived');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    } finally {
      setBusyId(null);
    }
  };

  const restore = async (rep) => {
    setBusyId(rep.id);
    try {
      await axios.post(`${API}/development-reports/${rep.id}/restore`);
      toast.success('Report restored');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (rep) => {
    if (!window.confirm('Permanently delete this draft? This cannot be undone.')) return;
    setBusyId(rep.id);
    try {
      await axios.delete(`${API}/development-reports/${rep.id}`);
      toast.success('Draft deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-6" data-testid="development-reports-page">
        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2">
              <FileText className="h-6 w-6 text-emerald-600" /> Player Development Reports
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">Prepare, review and finalize athlete development reports for players and parents.</p>
          </div>
          {canEdit && (
            <Button onClick={() => setShowCreate(true)} data-testid="create-report-btn">
              <Plus className="mr-1.5 h-4 w-4" /> Create Development Report
            </Button>
          )}
        </div>

        <div className="mb-4 flex flex-wrap gap-3">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-md border border-slate-200 px-3 py-1.5 text-sm" data-testid="filter-status">
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="finalized">Finalized</option>
            <option value="archived">Archived</option>
          </select>
          <select value={athleteFilter} onChange={(e) => setAthleteFilter(e.target.value)} className="rounded-md border border-slate-200 px-3 py-1.5 text-sm max-w-[220px]" data-testid="filter-athlete">
            <option value="all">All athletes</option>
            {athletes.map((a) => <option key={a.id} value={a.id}>{a.full_name}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 py-16 text-center">
            <FileText className="mx-auto mb-2 h-8 w-8 text-slate-300" />
            <p className="text-slate-500">No development reports yet.</p>
            {canEdit && <Button variant="outline" className="mt-3" onClick={() => setShowCreate(true)}>Create the first report</Button>}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reports-grid">
            {visible.map((rep) => {
              const a = athleteMap[rep.athlete_id] || {};
              const showMenu = (canDuplicate) || (canArchive && rep.status === 'finalized')
                || (canRestore && rep.status === 'archived') || (canDeleteDraft && rep.status === 'draft');
              return (
                <div
                  key={rep.id}
                  className="relative flex flex-col rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:border-emerald-300 hover:shadow-sm"
                  data-testid={`report-card-${rep.id}`}
                >
                  {showMenu && (
                    <div className="absolute right-2 top-2 z-10" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                            disabled={busyId === rep.id}
                            data-testid={`report-card-menu-${rep.id}`}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canDuplicate && (
                            <DropdownMenuItem onClick={() => duplicate(rep)} data-testid={`report-card-duplicate-${rep.id}`}>
                              <Copy className="mr-2 h-3.5 w-3.5" /> Duplicate
                            </DropdownMenuItem>
                          )}
                          {canArchive && rep.status === 'finalized' && (
                            <DropdownMenuItem onClick={() => archive(rep)} data-testid={`report-card-archive-${rep.id}`}>
                              <Archive className="mr-2 h-3.5 w-3.5" /> Archive
                            </DropdownMenuItem>
                          )}
                          {canRestore && rep.status === 'archived' && (
                            <DropdownMenuItem onClick={() => restore(rep)} data-testid={`report-card-restore-${rep.id}`}>
                              <ArchiveRestore className="mr-2 h-3.5 w-3.5" /> Restore
                            </DropdownMenuItem>
                          )}
                          {canDeleteDraft && rep.status === 'draft' && (
                            <DropdownMenuItem onClick={() => remove(rep)} className="text-rose-600" data-testid={`report-card-delete-${rep.id}`}>
                              <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete Draft
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                  <button onClick={() => openReport(rep)} className="flex flex-col text-left" data-testid={`report-card-open-${rep.id}`}>
                    <div className="flex items-center gap-3">
                      <AthleteAvatar photoUrl={a.profile_photo} thumbUrl={a.profile_photo_thumbnail} name={rep.athlete_name} size="md" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-slate-800">{rep.athlete_name}</div>
                        <div className="truncate text-xs text-slate-400">{a.position || rep.athlete_squad || '—'}</div>
                      </div>
                      <ReportStatusBadge status={rep.status} />
                    </div>
                    <div className="mt-3 space-y-1 text-sm">
                      <Row label="Review Period" value={`${fmtDate(rep.review_start_date)} – ${fmtDate(rep.review_end_date)}`} />
                      <Row label="Report Date" value={fmtDate(rep.report_date)} />
                      <Row label="Author" value={rep.created_by_name || '—'} />
                      <Row label="Updated" value={fmtDateTime(rep.updated_at || rep.created_at)} />
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CreateReportDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        athletes={athletes}
        hasModuleAccess={hasModuleAccess}
        isAdmin={isAdmin}
        seasons={seasons}
        onCreate={create}
        creating={creating}
      />
    </Layout>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-2">
    <span className="text-slate-400">{label}</span>
    <span className="truncate font-medium text-slate-700">{value}</span>
  </div>
);

export default DevelopmentReports;
